import { BulkResizeImage } from "@/store/features/bulkResize/types";
import { useAppSelector } from "store/store";
import { validateResizeDimension } from "client/utils/validateResizeDimension";
import {
    MAX_RESIZE_SIZE_FOR_WEBP_IN_PX,
    MAX_RESIZE_SIZE_IN_PX,
} from "store/features/bulkResize/utils/validateResizeDimensions";
import { convertToPixels } from "@/utils/pixels/convertToPixels";
import { translateFunc } from "@/utils/translateFunc";
import { roundFloat } from "@/utils/roundFloat";
import { convertFromPixels } from "@/utils/pixels/convertFromPixels";

export const useDimensionsValidity = (image: BulkResizeImage) => {
    const dpi = useAppSelector((store) => store.bulkResize.resizeSettings.dpi);
    const outputExt = useAppSelector((store) => {
        const saveAs = store.bulkResize.exportSettings.saveAs;
        if (saveAs === "original") {
            return image.format.replace(".", "");
        }
        return saveAs.replace(".", "");
    });
    const dimensionType = useAppSelector((store) => store.bulkResize.resizeSettings.dimensionType);
    const unit = useAppSelector((store) => store.bulkResize.resizeSettings.unit);
    const byPercentage = useAppSelector((store) => store.bulkResize.resizeSettings.byPercentage);
    const maxSize = outputExt === "webp" ? MAX_RESIZE_SIZE_FOR_WEBP_IN_PX : MAX_RESIZE_SIZE_IN_PX;
    const dimensionsValid = validateResizeDimension(
        image,
        dimensionType,
        unit,
        byPercentage.percentage,
        maxSize,
        dimensionType === "percentage" || unit === "pixels" ? undefined : +dpi,
    );
    const widthValid = convertToPixels(image.preview.width, unit, unit === "pixels" ? undefined : +dpi) <= maxSize;
    const heightValid = convertToPixels(image.preview.height, unit, unit === "pixels" ? undefined : +dpi) <= maxSize;
    const dimensionsErrorMessage =
        widthValid && heightValid
            ? ""
            : `${translateFunc("maximum-allowed-value-for", {
                  width_or_height:
                      !widthValid && !heightValid
                          ? `${translateFunc("width")} / ${translateFunc("height")}`
                          : !widthValid
                            ? translateFunc("width")
                            : !heightValid
                              ? translateFunc("height")
                              : "",
                  size: roundFloat(convertFromPixels(maxSize, unit, unit === "pixels" ? undefined : +dpi), false),
                  unit: translateFunc(unit),
              })} ${unit !== "pixels" ? translateFunc("at-dpi", { dpi }) : ""}`;

    return {
        dimensionsValid,
        maxSize,
        widthValid,
        heightValid,
        dimensionsErrorMessage,
    };
};
